import isEqual from 'lodash/isEqual';

import { Config } from '../../video-data/config';
import { Migrator, VERSION } from '../../video-data/migrations/migrator';
import uuid from '../../video-data/utils/uuid';
import {
    assertArray,
    assertBoolean,
    assertHasOwnProperties,
    assertIsValidAlpha,
    assertIsValidIndex,
    assertIsValidUrl,
    assertMinimumLength,
    assertNumberBetween,
    assertObject,
    assertString,
    assertTemplateWatermarkSource,
} from '../../validators';
import { cloneDeep, cloneDeepIfEqualTo } from '../../libs/cloneDeep';
import type { BaseSettings } from '../TimeAwareBase';
import { TimeInvariantBase } from '../TimeInvariantBase';
import { TemplateWatermarkManager } from '../watermark/TemplateWatermarkSettings';
import type { WatermarkThemeRef } from '../attributes/template/common';
import {
    createThemeRef,
    getTemplateIndex,
    getTemplateProperty,
    isTemplateReference,
    ThemeRefTypes,
} from '../attributes/template/common';
import type { OpaqueVideoData } from '../../video-data/types';
import { ColorGradingSettings } from '../ColorGradingSettings';
import type { TemplateColor, TemplateWatermarkSource } from './types';
import { TemplateColorProperties, TemplateWatermarkProperties } from './types';

const DEFAULT_PALETTE_COLOR: TemplateColor = {
    color: 0xffffff,
    alpha: 1,
    alphaEditable: false,
};

/**
 * Scaffolds our theming options (colours and fonts). This is where we define the colours and fonts that will be used
 * in the video.
 */
export class Template extends TimeInvariantBase {
    override _classType = 'Template';
    override _name = 'Template';
    #id = uuid();
    #transitionsEnabled = true;
    #fonts: string[] = [Config.DEFAULT_FONT_URL];
    #colors: TemplateColor[] = [DEFAULT_PALETTE_COLOR];
    #tag: string = null;
    #grading: ColorGradingSettings | null = null;

    #watermarkSources: TemplateWatermarkSource[] = [];
    watermark: TemplateWatermarkManager;

    constructor(settings: BaseSettings) {
        super(settings);

        this.watermark = new TemplateWatermarkManager({
            parent: this,
            fetchResources: false,
        });
    }

    override load(dataObj) {
        // migrate dataObj before loading
        dataObj = Migrator.updateTemplateData(dataObj);

        // if the parent does not allow the template id to change, enforce that here
        // `canChangeTemplates` only exists on some parent types - cast needed for optional property access
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (this.parent && (this.parent as any).canChangeTemplates !== undefined) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (this.id !== dataObj.id && !(this.parent as any).canChangeTemplates) {
                throw Error('Cannot apply the template config, canChangeTemplates is false.');
            }
        }
        return super.load(dataObj);
    }

    _gradingCustomLoad(grading: ColorGradingSettings | null) {
        if (!grading) {
            return;
        }
        this.addGrading();
        this.#grading.load(grading);
    }

    /**
     * Helper method to duplicate template data, without having to access the json outside of luminary
     */
    static duplicateTemplateData(
        templateData: OpaqueVideoData,
        newTemplateId: string,
    ): OpaqueVideoData {
        const newTemplateData = cloneDeepIfEqualTo(
            Migrator.updateTemplateData(templateData),
            templateData,
        );

        newTemplateData.id = newTemplateId;
        return newTemplateData;
    }

    /**
     * Helper method to get the colors from a template data object
     */
    static getColorsFromTemplateData(templateData: OpaqueVideoData): TemplateColor[] {
        const migratedTemplateData = cloneDeepIfEqualTo(
            Migrator.updateTemplateData(templateData),
            templateData,
        );
        return migratedTemplateData.colors;
    }

    /**
     * Helper method to get the fonts from a template data object
     */
    static getFontsFromTemplateData(templateData: OpaqueVideoData): string[] {
        const migratedTemplateData = cloneDeepIfEqualTo(
            Migrator.updateTemplateData(templateData),
            templateData,
        );
        return migratedTemplateData.fonts;
    }

    /**
     * Helper method to get the watermark sources from a template data object
     */
    static getWatermarkSourcesFromTemplateData(
        templateData: OpaqueVideoData,
    ): TemplateWatermarkSource[] {
        const migratedTemplateData = cloneDeepIfEqualTo(
            Migrator.updateTemplateData(templateData),
            templateData,
        );
        return migratedTemplateData.watermarkSources;
    }

    /**
     * The active template id. This either gets loaded through [setTemplate]{@link Luminary.setTemplate} or by loading a
     * video using [load]{@link Luminary.load}. Since we store this value into a database this property can not be null
     * and must be unique which is why we initialize it as an uuid.
     */
    override get id() {
        return this.#id;
    }

    /**
     * The tag for categorizing templates.
     */
    get tag(): string {
        return this.#tag;
    }

    set tag(value: string) {
        if (value === this.#tag) {
            return;
        }
        assertString(value, true);
        this.#tag = value;
        this.requiresUpdate = true;
    }

    /**
     * List of font urls for the template palette.
     */
    get fonts(): string[] {
        return this.#fonts;
    }

    set fonts(fonts: string[]) {
        if (isEqual(fonts, this.#fonts)) {
            return;
        }
        assertArray(fonts);
        assertMinimumLength(fonts, 1);
        fonts.forEach((font) => assertIsValidUrl(font));
        this.#fonts = fonts;
        this.requiresUpdate = true;
    }

    /**
     * List of colors for the template palette.
     */
    get colors(): TemplateColor[] {
        return this.#colors;
    }

    set colors(colors: TemplateColor[]) {
        if (isEqual(colors, this.#colors)) {
            return;
        }
        assertArray(colors);
        assertMinimumLength(colors, 1);
        colors.forEach((c) => {
            assertObject(c);
            assertHasOwnProperties(c, [
                TemplateColorProperties.COLOR,
                TemplateColorProperties.ALPHA,
                'alphaEditable',
            ]);
            assertNumberBetween(c.color, 0, 0xffffff);
            assertNumberBetween(c.alpha, 0, 1);
            assertBoolean(c.alphaEditable);
        });
        this.#colors = colors;
        this.requiresUpdate = true;
    }

    addGrading() {
        if (this.#grading) {
            return;
        }
        this.#grading = new ColorGradingSettings({
            parent: this,
            fetchResources: this.fetchResources,
        });
    }

    removeGrading() {
        if (!this.#grading) {
            return;
        }
        this.#grading.destroy();
        this.#grading = null;
        this.requiresUpdate = true;
    }

    get grading(): ColorGradingSettings | null {
        return this.#grading;
    }

    /**
     * List of watermark sources.
     */
    get watermarkSources(): TemplateWatermarkSource[] {
        return this.#watermarkSources;
    }

    /**
     * Setting the watermark sources requires that the deleted sources are not in use by any scenes or set as
     * `watermark.sourceData`. Any scenes with valid template references will reference the new watermark sources.
     *
     * It is better to use the `removeWatermarkSourceAtIndex` which manages existing references.
     */
    set watermarkSources(watermarkSources: TemplateWatermarkSource[]) {
        if (isEqual(this.#watermarkSources, watermarkSources)) {
            return;
        }

        const templateRef = getTemplateProperty(
            this.watermark,
            TemplateWatermarkProperties.SOURCE_DATA,
        );
        if (isTemplateReference(templateRef)) {
            const refIndex = getTemplateIndex(templateRef);
            assertIsValidIndex(refIndex, watermarkSources.length);
        }

        assertArray(watermarkSources);
        watermarkSources.forEach((source) => {
            assertTemplateWatermarkSource(source);
        });

        this.root?.scenes.forEach((scene) => {
            const sceneTemplateRef = getTemplateProperty(
                scene.watermark,
                TemplateWatermarkProperties.SOURCE_DATA,
            );
            if (isTemplateReference(sceneTemplateRef)) {
                const index = getTemplateIndex(sceneTemplateRef);
                assertIsValidIndex(index, watermarkSources.length);
            }
        });
        this.#watermarkSources = watermarkSources;
        this.requiresUpdate = true;
    }

    /**
     * Replaces the existing watermarks safely, checking and fixing scenes that might reference watermarks that
     * have been removed.
     */
    replaceWatermarkSources(watermarkSources: TemplateWatermarkSource[]) {
        const contains = (
            watermarkSources: TemplateWatermarkSource[],
            source: TemplateWatermarkSource,
        ) => {
            return !!watermarkSources.find((s) => isEqual(s, source));
        };

        const currentSources = this.watermarkSources;
        const removedIndices = currentSources.reduce((acc, currentSource, index) => {
            if (!contains(watermarkSources, currentSource)) {
                acc.push(index);
            }
            return acc;
        }, [] as number[]);

        removedIndices.reverse().forEach((index) => this.removeWatermarkSourceAtIndex(index));
        this.watermarkSources = watermarkSources;
    }

    _watermarkSourcesCustomLoad(watermarkSources: TemplateWatermarkSource[]) {
        // Required to skip assertions during deserialization, where the getter would have partial state.
        this.#watermarkSources = watermarkSources;
    }

    get transitionsEnabled() {
        return this.#transitionsEnabled;
    }

    set transitionsEnabled(transitionsEnabled) {
        if (this.#transitionsEnabled === transitionsEnabled) {
            return;
        }
        assertBoolean(transitionsEnabled);
        this.#transitionsEnabled = transitionsEnabled;
        this.requiresUpdate = true;
    }

    /**
     * The current API version
     */
    get version(): string {
        return VERSION;
    }

    /**
     * Set a new font for the given index in the fonts list.
     *
     * Note that changing a font here will create a new internal fonts array. This is
     * because if we just mutate an entry in place, it's very difficult on the react side
     * to do equality comparisons across renders since a mutation changes the before value
     * as well (arrays are pass by reference)
     */
    setFontAtIndex(font: string, targetIndex: number) {
        if (this.#fonts[targetIndex] === font) {
            return;
        }
        assertString(font);
        assertMinimumLength(this.fonts, targetIndex + 1);
        const fonts = cloneDeep(this.#fonts);
        fonts[targetIndex] = font;
        this.#fonts = fonts;
        this.requiresUpdate = true;
    }

    /**
     * Set a new color for the given index in the colors list.
     *
     * Note that changing a color here will create a new internal colors array. This is
     * because if we just mutate an entry in place, it's very difficult on the react side
     * to do equality comparisons across renders since a mutation changes the before value
     * as well (arrays are pass by reference)
     */
    setColorAtIndex(color: number, targetIndex: number, alpha = null) {
        assertNumberBetween(color, 0, 0xffffff);
        assertMinimumLength(this.colors, targetIndex + 1);
        assertIsValidAlpha(alpha, true);
        if (
            this.#colors[targetIndex].color !== color ||
            (alpha !== null && this.#colors[targetIndex].alpha !== alpha)
        ) {
            this.#colors = cloneDeep(this.#colors);
            this.#colors[targetIndex] = { ...this.#colors[targetIndex], color };
            if (alpha !== null) {
                if (this.#colors[targetIndex].alphaEditable) {
                    this.#colors[targetIndex].alpha = alpha;
                } else if (this.#colors[targetIndex].alpha !== alpha) {
                    throw new Error(`Changing alpha for color ${targetIndex} is not permitted`);
                }
            }
            this.requiresUpdate = true;
        }
    }

    /**
     * Sets if the alpha is editable for a color at the target index.
     *
     * Note that changing editable alpha here will create a new internal colors array. This is
     * because if we just mutate an entry in place, it's very difficult on the react side
     * to do equality comparisons across renders since a mutation changes the before value
     * as well (arrays are pass by reference)
     */
    setAlphaEditableAtIndex(alphaEditable: boolean, targetIndex: number) {
        assertBoolean(alphaEditable);
        assertMinimumLength(this.colors, targetIndex + 1);
        if (this.#colors[targetIndex].alphaEditable !== alphaEditable) {
            this.#colors = cloneDeep(this.#colors);
            this.#colors[targetIndex] = { ...this.#colors[targetIndex], alphaEditable };
        }
    }

    /**
     * Adds a new watermark source to the end of watermarkSources.
     */
    addWatermarkSource(watermarkSource: TemplateWatermarkSource) {
        assertTemplateWatermarkSource(watermarkSource);
        this.#watermarkSources = cloneDeep(this.#watermarkSources);
        this.#watermarkSources.push(watermarkSource);
        this.requiresUpdate = true;
    }

    /**
     * Set a template watermark at the given index
     *
     * This clones the array to enable shallow comparisons.
     */
    setWatermarkSourceAtIndex(watermarkSource: TemplateWatermarkSource, index: number) {
        if (isEqual(watermarkSource, this.#watermarkSources[index])) {
            return;
        }
        assertIsValidIndex(index, this.#watermarkSources.length);
        assertTemplateWatermarkSource(watermarkSource);
        this.#watermarkSources = cloneDeep(this.#watermarkSources);
        this.#watermarkSources[index] = watermarkSource;

        this.requiresUpdate = true;
    }

    /**
     * Remove a watermark at the given index.
     *
     * It also updates any watermark references to accommodate the removal. So if a reference
     * exists that watermark will be set to NONE, while references with an index greater than
     * the one being removed will be reduced so the underlying sourceData resolved by the
     * reference remains the same.
     *
     * This clones the array to enable shallow comparisons.
     */
    removeWatermarkSourceAtIndex(index: number) {
        assertIsValidIndex(index, this.#watermarkSources.length);
        assertMinimumLength(this.#watermarkSources, 1);

        const getNewRef = (
            currentRef: unknown,
        ): WatermarkThemeRef | typeof Config.WATERMARK_NONE => {
            if (!isTemplateReference(currentRef)) {
                return currentRef as WatermarkThemeRef;
            }
            const templateRefIndex = getTemplateIndex(currentRef);
            if (templateRefIndex === index) {
                // Remove the watermark from any linked refs as well
                return Config.WATERMARK_NONE;
            } else if (templateRefIndex > index) {
                // Because removing the index shifts the array we need to update the reference index
                return createThemeRef(ThemeRefTypes.WATERMARK_SOURCES, templateRefIndex - 1);
            } else {
                return currentRef;
            }
        };

        // batch
        const initialAutoUpdate = this.root.autoUpdate;
        this.root.autoUpdate = false;

        // Update scenes
        this.root.scenes.forEach((scene) => {
            const sceneTemplateRef = getTemplateProperty(
                scene.watermark,
                TemplateWatermarkProperties.SOURCE_DATA,
            );
            scene.watermark.sourceData = getNewRef(sceneTemplateRef);
        });

        // Update template
        const templateWatermarkRef = getTemplateProperty(
            this.watermark,
            TemplateWatermarkProperties.SOURCE_DATA,
        );
        this.watermark.sourceData = getNewRef(templateWatermarkRef);

        this.#watermarkSources = this.#watermarkSources.filter(
            (_watermark, watermarkIndex) => watermarkIndex !== index,
        );

        this.requiresUpdate = true;
        this.root.autoUpdate = initialAutoUpdate;
    }

    override _update() {
        if (!Config.isScene(this.parent)) {
            return;
        }

        // update all assets that are referenced to this template
        this.parent._updateAllTemplateReferencedAssets();
        this.parent.watermark.requiresUpdate = true;
    }

    override _notifyAfterUpdate() {
        if (Config.isScene(this.parent)) {
            return this._emitChanged();
        }
        super._notifyAfterUpdate();
    }

    override clear() {
        this.#id = uuid();
        this.#transitionsEnabled = true;
        this.#fonts = [Config.DEFAULT_FONT_URL];
        this.#colors = [DEFAULT_PALETTE_COLOR];
        this.#tag = null;
    }

    _idCustomLoad(id: string) {
        if (id === this.#id) {
            return;
        }
        assertString(id);
        this.#id = id;
        this.requiresUpdate = true;
    }

    override _checkAndFixProperties() {
        super._checkAndFixProperties();
        // Ensure non-editable alpha values are set to 1
        this.#colors.forEach((color) => {
            if (!color.alphaEditable && color.alpha !== 1) {
                color.alpha = 1;
            }
        });
    }

    override get propertiesToSerialize() {
        return [
            'id',
            'transitionsEnabled',
            'fonts',
            'colors',
            'tag',
            'watermarkSources',
            'watermark',
            'grading',
        ];
    }

    override get readonlyProperties() {
        return ['version'];
    }
}
